import {
  Viewer,
  Terrain,
  Cartesian3,
  HeadingPitchRoll,
  createGooglePhotorealistic3DTileset,
} from 'cesium'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { cesiumConfig } from '@/config/cesium.config'
import type { DroneLocation } from '@/types/dashboard.types'
import { useMapFunctions } from '@/composables/useMapFunctions'
import log from '@/utils/useLogger'
import { setGoogle2DImagery, setMapboxImagery, clearLayerCache } from './shared'
import useUserSettings from '@/composables/useUserSettings'
// Create a singleton instance
class FPVServiceClass {
  public fpvViewer = ref<Viewer | null>(null)
  public fpvTileset = ref<any>(null)
  public showFPV = ref(false)

  //commented on fpv service for now until we have a new fpv service
  public async initializeFPV(
    fpvElement: Ref<HTMLElement | null>,
    droneLocation: DroneLocation,
  ): Promise<void> {
    if (!fpvElement.value) return

    // Clean up existing FPV viewer if it exists
    this.cleanup()

    // Add a safety check
    if (!fpvElement.value) {
      log.warn('FPV element not available for initialization')
      return
    }

    try {
      const { settings } = useUserSettings()

      const { base, overlay } = await setGoogle2DImagery()

      this.fpvViewer.value = new Viewer(fpvElement.value, {
        baseLayer: false,
        terrain: Terrain.fromWorldTerrain(),
        baseLayerPicker: false,
        animation: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        timeline: false,
        fullscreenButton: false,
        infoBox: false,
        selectionIndicator: false,
        geocoder: false,
        sceneMode: cesiumConfig.defaultSceneMode,
        useBrowserRecommendedResolution: true,
      })

      // Add terrain and imagery
      if (this.fpvViewer.value) {
        if (settings.value.mapStyle === 'google-maps') {
          this.fpvViewer.value.imageryLayers.add(base)
          this.fpvViewer.value.imageryLayers.add(overlay)
        } else {
          const mapBoxStyle = await setMapboxImagery(settings.value.mapStyle)
          this.fpvViewer.value.imageryLayers.addImageryProvider(mapBoxStyle)
        }
        const { getTerrainHeight } = useMapFunctions()
        const terrainHeight = await getTerrainHeight(
          droneLocation.longitude,
          droneLocation.latitude,
        )
        this.fpvViewer.value.camera.setView({
          destination: Cartesian3.fromDegrees(
            droneLocation.longitude,
            droneLocation.latitude,
            Number(terrainHeight) || 10,
          ),
        })
        this.fpvViewer.value.scene.globe.enableLighting = true
        this.fpvViewer.value.scene.globe.tileCacheSize = 20
        this.fpvViewer.value.cesiumWidget.creditContainer.className = 'display-none'

        // Add Google Photorealistic 3D Tiles if enabled
        const show3dTiles = settings.value.tiles3dMode && settings.value.tiles3dMode !== 'none'
        await this.toggleFpvTileset(show3dTiles)

        // Mark as showing
        this.showFPV.value = true
      }
    } catch (error) {
      log.error('Error initializing FPV viewer:', error)
      this.cleanup()
    }
  }

  public async toggleFpvTileset(show: boolean): Promise<boolean> {
    if (!this.fpvViewer.value) return false

    try {
      if (show) {
        if (this.fpvTileset.value) {
          this.fpvTileset.value.show = true
        } else {
          const tileset = await createGooglePhotorealistic3DTileset({
            onlyUsingWithGoogleGeocoder: true,
          })
          this.fpvViewer.value?.scene.primitives.add(tileset)
          this.fpvTileset.value = tileset
        }
        return true
      } else {
        if (this.fpvTileset.value) {
          this.fpvTileset.value.show = false
        }
        return true
      }
    } catch (err) {
      log.error('Error toggling FPV tileset:', err)
      return false
    }
  }

  public updateFPVCamera(position: Cartesian3, orientation: HeadingPitchRoll): void {
    if (!this.fpvViewer.value || !this.showFPV.value) return

    try {
      this.fpvViewer.value.scene.camera.setView({
        destination: position,
        orientation: {
          heading: orientation.heading,
          pitch: orientation.pitch,
          roll: orientation.roll,
        },
      })
    } catch (error) {
      log.error('Error updating FPV camera:', error)
    }
  }

  public cleanup(): void {
    if (this.fpvViewer.value && !this.fpvViewer.value.isDestroyed()) {
      try {
        // Clear layer cache before destroying viewer
        clearLayerCache(this.fpvViewer.value)
        this.fpvViewer.value.destroy()
        this.fpvViewer.value = null
      } catch (error) {
        log.error('Error destroying FPV viewer:', error)
      }
    }

    this.fpvViewer.value = null
    this.fpvTileset.value = null
  }
}

// Export a singleton instance
export const FPVService = new FPVServiceClass()
